import { useMemo } from 'react'
import { Box, SxProps, Theme } from '@mui/material'
import DOMPurify from 'dompurify'

import StyledWrap from 'components/StyledWrap'

interface FooterProps {
  content: string
}

/**
 * Sanitizes the admin-provided footer HTML. Links are preserved (and forced to
 * open safely), everything else falls back to DOMPurify's default html profile.
 */
function sanitizeFooterHtml(html: string): string {
  const forceSafeLinks = (node: Element): void => {
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer')
    }
  }

  DOMPurify.addHook('afterSanitizeAttributes', forceSafeLinks)

  try {
    return DOMPurify.sanitize(html, {
      ADD_ATTR: ['target', 'rel'],
      USE_PROFILES: { html: true },
    })
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes')
  }
}

// Default styling — content centered. Overridable per-theme via the "Footer"
// styling component (StyledWrap appends theme.stylings.Footer below this).
const defaultFooterStyle: SxProps<Theme> = {
  width: '100%',
  textAlign: 'center',
  padding: '24px 16px',
  backgroundColor: 'common.white',
  borderTop: '1px solid',
  borderColor: 'rgba(17, 24, 39, 0.08)',
  color: 'text.secondary',
  fontSize: '0.875rem',
  '& a': {
    color: 'inherit',
    textDecoration: 'none',
  },
  '& a:hover': {
    textDecoration: 'none',
  },
}

export default function Footer({ content }: FooterProps): JSX.Element | null {
  const sanitized = useMemo(() => sanitizeFooterHtml(content ?? ''), [content])

  if (sanitized.trim() === '') {
    return null
  }

  return (
    <StyledWrap elementName="Footer">
      <Box
        className="main-footer"
        component="footer"
        sx={defaultFooterStyle}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    </StyledWrap>
  )
}
